import * as yup from 'yup';
import { sanitizeInput } from './sanitize';

export const MAX_LENGTHS = {
  personName: 100,
  entityName: 150,
  identifier: 64,
  email: 254,
  username: 100,
  password: 128,
  url: 2048,
  phone: 20,
  ssn: 9,
  credential: 255,
  postalCode: 10,
  addressLine: 255,
  shortText: 500,
  longText: 2000,
  secret: 1024,
  css: 50000,
} as const;

export const PATTERNS = {
  personName: /^[\p{L} .'-]*$/u,
  entityName: /^[\p{L}\p{N} .,'_&()-]*$/u,
  identifier: /^[A-Za-z0-9._-]*$/,
  username: /^[A-Za-z0-9@._-]*$/,
  phone: /^[0-9 +()-]*$/,
  ssn: /^[0-9]*$/,
  postalCode: /^[0-9-]*$/,
  addressLine: /^[\p{L}\p{N} .,#'/-]*$/u,
} as const;

const MESSAGES = {
  personName: 'Only letters, spaces, and . - \' are allowed',
  entityName: 'Contains invalid characters',
  identifier: 'Only letters, numbers, dots, hyphens, and underscores are allowed',
  username: 'Contains invalid characters',
  phone: 'Enter a valid phone number',
  ssn: 'SSN must contain digits only',
  postalCode: 'Enter a valid postal code',
  addressLine: 'Contains invalid characters',
  url: 'Enter a valid URL',
  https: 'URL must start with https:// and contain at least one dot',
} as const;

const maxMessage = (label: string, max: number) => `${label} must be at most ${max} characters`;

export const sanitizedString = (options?: { multiline?: boolean }): yup.StringSchema =>
  yup
    .string()
    .transform((value) => sanitizeInput(value, { multiline: options?.multiline }) as string);

export const personNameField = (label = 'This field', max: number = MAX_LENGTHS.personName): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .matches(PATTERNS.personName, { message: MESSAGES.personName, excludeEmptyString: true });

export const entityNameField = (label = 'Name', max: number = MAX_LENGTHS.entityName): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .matches(PATTERNS.entityName, { message: MESSAGES.entityName, excludeEmptyString: true });

export const identifierField = (label = 'ID', max: number = MAX_LENGTHS.identifier): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .matches(PATTERNS.identifier, { message: MESSAGES.identifier, excludeEmptyString: true });

export const credentialField = (label = 'This field', max: number = MAX_LENGTHS.credential): yup.StringSchema =>
  sanitizedString().max(max, maxMessage(label, max));

export const emailField = (label = 'Email', max: number = MAX_LENGTHS.email): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .email('Invalid email format');

export const usernameField = (label = 'Username', max: number = MAX_LENGTHS.username): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .matches(PATTERNS.username, { message: MESSAGES.username, excludeEmptyString: true });

export const passwordField = (options?: { min?: number; max?: number; label?: string }): yup.StringSchema => {
  const label = options?.label ?? 'Password';
  const max = options?.max ?? MAX_LENGTHS.password;
  let schema = sanitizedString().max(max, maxMessage(label, max));
  if (options?.min) {
    schema = schema.min(options.min, `${label} must be at least ${options.min} characters`);
  }
  return schema;
};

export const urlField = (options?: { label?: string; requireHttps?: boolean; max?: number }): yup.StringSchema => {
  const label = options?.label ?? 'URL';
  const max = options?.max ?? MAX_LENGTHS.url;
  let schema = sanitizedString().max(max, maxMessage(label, max));
  if (options?.requireHttps) {
    schema = schema.test(
      'is-https-url',
      MESSAGES.https,
      (value) => !value || (value.startsWith('https://') && value.includes('.')),
    );
  } else {
    schema = schema.url(MESSAGES.url);
  }
  return schema;
};

export const phoneField = (label = 'Phone', max: number = MAX_LENGTHS.phone): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .matches(PATTERNS.phone, { message: MESSAGES.phone, excludeEmptyString: true });

export const ssnField = (label = 'SSN'): yup.StringSchema =>
  sanitizedString()
    .max(MAX_LENGTHS.ssn, maxMessage(label, MAX_LENGTHS.ssn))
    .matches(PATTERNS.ssn, { message: MESSAGES.ssn, excludeEmptyString: true });

export const postalCodeField = (label = 'Postal code'): yup.StringSchema =>
  sanitizedString()
    .max(MAX_LENGTHS.postalCode, maxMessage(label, MAX_LENGTHS.postalCode))
    .matches(PATTERNS.postalCode, { message: MESSAGES.postalCode, excludeEmptyString: true });

export const addressLineField = (label = 'Address', max: number = MAX_LENGTHS.addressLine): yup.StringSchema =>
  sanitizedString()
    .max(max, maxMessage(label, max))
    .matches(PATTERNS.addressLine, { message: MESSAGES.addressLine, excludeEmptyString: true });

export const shortTextField = (label = 'This field', max: number = MAX_LENGTHS.shortText): yup.StringSchema =>
  sanitizedString().max(max, maxMessage(label, max));

export const longTextField = (label = 'This field', max: number = MAX_LENGTHS.longText): yup.StringSchema =>
  sanitizedString({ multiline: true }).max(max, maxMessage(label, max));

export const secretField = (label = 'This field', max: number = MAX_LENGTHS.secret): yup.StringSchema =>
  sanitizedString().max(max, maxMessage(label, max));
